import React from "react";
import styled from "styled-components";

const StyledButton = styled.button`
  /* Base styles */
  border: none;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  font-size: inherit;
  border-radius: 0.75rem;
  outline: none;
  height: 44px;

  /* Variant-specific styles */
  ${({ $variant, $active, $customColor }) => {
    switch ($variant) {
      case "primary":
        return `
          background: #000000;
          color: white;
          padding: 0.75rem 1.5rem;

          &:hover:not(:disabled) {
            opacity: 0.7;
          }
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
        `;

      case "secondary":
        return `
          background: #f8f9fa;
          color: #333;
          border: 2px solid #e1e5e9;
          padding: 0.75rem 1.5rem;
          
          &:hover {
            background: #e9ecef;
            border-color: #dee2e6;
          }
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `;

      case "header":
        return `
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.3);
          padding: 0.5rem 1rem;
          font-weight: 500;
          
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.5);
          }
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `;

      case "link":
        return `
          background: none;
          color: #000000;
          text-decoration: underline;
          padding: 0;
          font-weight: inherit;
          
          &:hover {
            color: #333333;
          }
        `;

      case "auth-toggle":
        return `
          flex: 1;
          padding: 0.75rem;
          background: ${$active ? "#000000" : "transparent"};
          color: ${$active ? "white" : "#6c757d"};
          font-weight: 500;
          
          &:hover {
            background: ${$active ? "#000000" : "rgba(0, 0, 0, 0.1)"};
            color: ${$active ? "white" : "#333333"};
          }

          &:active {
            transform: scale(0.9);
          }
        `;

      case "chat":
        return `
          background: ${$customColor || "#667eea"};
          color: white;
          padding: 0.75rem 1.5rem;
          width: 100%;
          
          &:hover:not(:disabled) {
            opacity: 0.9;
          }
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
        `;

      case "retry":
        return `
          background: #000000;
          color: white;
          padding: 0.75rem 1.5rem;
          margin-top: 1rem;
        
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
        `;

      case "danger":
        return `
          background: #dc3545;
          color: white;
          padding: 0.75rem 1.5rem;

          &:hover:not(:disabled) {
            background: #c82333;
          }
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
        `;

      default:
        return `
          background: #000000;
          color: white;
          padding: 0.75rem 1.5rem;
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
        `;
    }
  }}

  /* Size variants */
  ${({ $size }) => {
    switch ($size) {
      case "small":
        return `
          padding: 0.5rem 1rem;
          font-size: 1rem;
        `;
      case "large":
        return `
          padding: 1rem 2rem;
          font-size: 1rem;
        `;
      default:
        return "";
    }
  }}
  
  /* Full width option */
  ${({ $fullWidth }) =>
    $fullWidth &&
    `
    width: 100%;
  `}
  
  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    ${({ $variant, $fullWidthOnMobile }) => {
      if ($fullWidthOnMobile && $variant === "primary") {
        return `width: 100%;`;
      }
      return "";
    }}
  }
`;

const Button = React.forwardRef(
  (
    {
      children,
      variant = "primary",
      size,
      active,
      customColor,
      fullWidth,
      fullWidthOnMobile,
      ...props
    },
    ref
  ) => {
    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $size={size}
        $active={active}
        $customColor={customColor}
        $fullWidth={fullWidth}
        $fullWidthOnMobile={fullWidthOnMobile}
        {...props}
      >
        {children}
      </StyledButton>
    );
  }
);

Button.displayName = "Button";

export default Button;
